/**
 * inventory-limit.js  —  Electro theme
 *
 * Inventory-based quantity restriction for the collection grid + quick view.
 * Covers:
 *   1. The <quantity-input> stepper  (PDP and AJAX-injected Quick View)
 *   2. Variant switching             (re-reads max from the inventory map)
 *   3. Collection card "quick add"   (blocks adds that would exceed stock)
 *
 * Works on AJAX-injected content because every listener is delegated on
 * `document`. No per-instance init is required.
 *
 * Liquid data contract:
 *   - input[name="quantity"]      gets  max="N"  only when the variant is limited
 *   - script.js-inventory-map     holds { "<variantId>": <max|null> }  (null = unlimited)
 *   - form.js-form-add-to-cart     gets  data-inventory-max="N"  on cards  (-1 = unlimited)
 *
 * Note: Shopify already rejects oversell at /cart/add and /cart/change when
 * tracking is on and policy is "deny". This file is the UX layer on top of that
 * hard backstop, and it always FAILS OPEN so a real purchase is never blocked.
 */
(function () {
  'use strict';

  var QTY_SEL  = 'input[name="quantity"]';
  var PLUS_SEL = 'quantity-input-plus';
  var WRAP_SEL = 'quantity-input';

  function toInt(v, fallback) {
    var n = parseInt(v, 10);
    return isNaN(n) ? fallback : n;
  }

  function getMap(scope) {
    var el = scope && scope.querySelector('.js-inventory-map');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* ---- 1. Stepper: stop the + button at the inventory ceiling ----
     Capture phase => we run BEFORE the theme's own <quantity-input-plus> handler. */
  document.addEventListener('click', function (e) {
    var plus = e.target.closest(PLUS_SEL);
    if (!plus) return;

    var wrap  = plus.closest(WRAP_SEL);
    var input = wrap && wrap.querySelector(QTY_SEL);
    if (!input || !input.hasAttribute('max')) return; // unlimited -> let theme increment

    var max = toInt(input.getAttribute('max'), Infinity);
    if (toInt(input.value, 1) >= max) {
      e.preventDefault();
      e.stopImmediatePropagation();   // block the theme's increment
      flashLimit(wrap, max);
    }
  }, true);

  /* Safety net: clamp the value after any change on a limited stepper. */
  document.addEventListener('change', function (e) {
    var input = e.target.closest(QTY_SEL);
    if (!input || !input.hasAttribute('max')) return;
    var max = toInt(input.getAttribute('max'), Infinity);
    if (toInt(input.value, 1) > max) input.value = max < 1 ? 1 : max;
  });

  /* ---- 2. Variant switch: refresh the stepper max from the inventory map ---- */
  document.addEventListener('change', function (e) {
    if (!e.target.closest('.js-option-item, select[name="id"]')) return;

    var form = e.target.closest('form.js-form-add-to-cart');
    if (!form) return;

    // Let the theme update #select-master first, then read the new variant id.
    setTimeout(function () {
      var master = form.querySelector('select[name="id"], input[name="id"]');
      var map    = getMap(form);
      var input  = form.querySelector(QTY_SEL);
      if (!master || !map || !input) return;

      var max = map[String(master.value)]; // number = limited, null/undefined = unlimited
      if (max === null || typeof max === 'undefined') {
        input.removeAttribute('max');
      } else {
        input.setAttribute('max', max);
        if (toInt(input.value, 1) > max) input.value = max < 1 ? 1 : max;
      }
    }, 0);
  });

  /* ---- 3. Card "quick add": block adds that would exceed available stock ----
     The add is async (we must check the cart), so we stop this click, validate,
     then replay the original click once on pass. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.js-atc-btn');
    if (!btn) return;

    var form = btn.closest('form.js-form-add-to-cart');
    if (!form || form.dataset.invChecked === '1') return; // replayed click -> let it pass

    var max = toInt(form.getAttribute('data-inventory-max'), -1);
    if (max < 0) return; // no card limit (PDP/quick-view form, or unlimited) -> let theme add

    var idInput   = form.querySelector('input[name="id"]');
    var variantId = idInput && String(idInput.value);
    if (!variantId) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var inCart = 0;
        (cart.items || []).forEach(function (it) {
          if (String(it.id) === variantId) inCart += it.quantity;
        });

        if (inCart + 1 > max) {
          flashLimit(form, max);
          return;
        }
        replay(form, btn);
      })
      .catch(function () { replay(form, btn); }); // fail open
  }, true);

  function replay(form, btn) {
    form.dataset.invChecked = '1';
    btn.click();
    delete form.dataset.invChecked;
  }

  /* ---- Small inline "Only N available" notice next to the control ---- */
  function flashLimit(scope, max) {
    if (!scope) return;
    var host = scope.closest('.line-item_quantity, .product-form_quantity, .product-card_actions, .input-group') || scope;
    var note = host.querySelector('.js-inventory-note');
    if (!note) {
      note = document.createElement('div');
      note.className = 'js-inventory-note';
      note.style.cssText = 'color:#c0392b;font-size:12px;margin-top:6px;';
      host.appendChild(note);
    }
    note.textContent = 'Only ' + max + ' available in stock';
    clearTimeout(note._t);
    note._t = setTimeout(function () { note.textContent = ''; }, 2500);
  }
})();